import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleHelp,
  Copy,
  Info,
  MessageCircleQuestion,
  RotateCcw,
  Send,
  Square,
  X,
  ArrowLeftRight,
  Lightbulb,
  Route,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ChatScopeFilters, formatPublicMessage } from "../../api";
import type { AskSuggestion } from "../../chat/askStarters";
import { ASK_ICONS } from "../../chat/askIcons";
import { CHAT_MAX_MESSAGES } from "../../api/openapi/chat-limits";
import { useChatDraft } from "../../chat/chatDraft";
import {
  CHAT_HARD_LIMIT_CHARS,
  CHAT_SOFT_LIMIT_CHARS,
  chatMessageCount,
  chatTranscriptChars,
  clearChatError,
  resetChat,
  sendChatMessage,
  stopChat,
  useChatSession,
} from "../../chat/chatStore";
import type { MentionCandidate, MentionOption } from "../watchlist/mentions";
import MentionMenu from "../watchlist/MentionMenu";
import { useMentions } from "../watchlist/useMentions";
import { ChatAiDisclaimer } from "./ChatAiDisclaimer";
import { ChatMarkdown } from "./ChatMarkdown";
import { ChatSelectionQuote, appendQuote } from "./ChatSelectionQuote";
import { QuoteTextarea } from "./QuoteTextarea";
import { chatThinkingTranslationKey } from "./thinking";
import { DetachedStreamingIndicator, useChatAutoScroll } from "./useChatAutoScroll";

export type ChatScope = {
  /** Shown in the scope chip and cited in scope-change notes. */
  label: string;
};

type ChatPanelProps = {
  /** Store key; transcripts persist per key until the page reloads. */
  sessionKey: string;
  /** Scope filters sent with each message to the unified chat endpoint. */
  filters: ChatScopeFilters;
  scope: ChatScope;
  context?: string;
  /** Removable, caller-owned context attached to every subsequent question. */
  contextItems?: { id: string; name: string }[];
  onRemoveContextItem?: (id: string) => void;
  /** Heading above the transcript; omit where the surrounding chrome already names the panel. */
  title?: string;
  /** Back arrow beside the title; omit where the surrounding chrome already offers a way out. */
  onBack?: () => void;
  /** Starter questions shown while the transcript is empty. */
  suggestions: (string | AskSuggestion)[];
  welcome?: { title: string };
  starterAction?: {
    label: string;
    onClick: () => void;
    kind: "kpi" | "compare" | "explore";
  };
  mentionOptions?: {
    users: MentionOption[];
    processes: MentionOption[];
    onPick: (candidate: MentionCandidate) => void;
  };
  /** Focus management: only an active (visible) panel grabs focus. */
  active: boolean;
  /** Scope controls shown above the text field, inside the composer. */
  composerScope?: React.ReactNode;
  composerActions?: React.ReactNode;
  onSend?: (text: string, scope?: { filters: ChatScopeFilters; label: string }) => void;
  disabled?: boolean;
};

/** The composer grows with its draft up to this height, then scrolls instead. */
const COMPOSER_MAX_HEIGHT = 160;
export const CHAT_CONTEXT_ITEM_MAX_CHARS = 20;

/** Keep context chips compact while retaining the full value in their title and request data. */
export const truncateChatContextItem = (name: string) =>
  name.length > CHAT_CONTEXT_ITEM_MAX_CHARS
    ? `${name.slice(0, CHAT_CONTEXT_ITEM_MAX_CHARS - 1)}…`
    : name;

/** Copy-to-clipboard control shown under each completed assistant reply. */
export const CopyButton = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied; fail silently.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? t("chat_copied") : t("chat_copy")}
      title={copied ? t("chat_copied") : t("chat_copy")}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-dim transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
};

const focusChatInput = (elem: HTMLTextAreaElement | null) => {
  elem?.focus({ preventScroll: true });
  const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  elem?.scrollIntoView({
    behavior: smooth ? "smooth" : "auto",
    block: "nearest",
  });
};

export const ChatPanel = ({
  sessionKey,
  filters,
  scope,
  context,
  contextItems = [],
  onRemoveContextItem,
  title,
  onBack,
  suggestions,
  welcome,
  starterAction,
  mentionOptions,
  active,
  composerScope,
  composerActions,
  onSend,
  disabled = false,
}: ChatPanelProps) => {
  const { t } = useTranslation();
  const session = useChatSession(sessionKey);
  const [draft, setDraft] = useChatDraft(sessionKey);
  const mentions = useMentions({
    text: draft,
    onTextChange: setDraft,
    userOptions: mentionOptions?.users ?? [],
    processOptions: mentionOptions?.processes ?? [],
    onPick: candidate => mentionOptions?.onPick(candidate),
  });
  const inputRef = mentions.textareaRef;
  const mentionTokens = [
    ...(mentionOptions?.users ?? []).filter(option =>
      option.selectAll
        ? !filters.userIds ||
          mentionOptions!.users
            .filter(user => !user.selectAll)
            .every(user => filters.userIds!.includes(user.id))
        : filters.userIds?.includes(option.id),
    ),
    ...(mentionOptions?.processes ?? []).filter(option =>
      option.selectAll ? !filters.processes : filters.processes?.includes(option.label),
    ),
  ].map(option => `@${option.label}`);

  const streaming = session.status === "streaming";
  const lastEntry = session.entries[session.entries.length - 1];
  const contentRevision = `${session.entries.length}:${
    lastEntry?.kind === "message" ? lastEntry.content.length : (lastEntry?.kind ?? "empty")
  }`;
  const { scrollRef, detached, scrollProps, scrollToBottom } = useChatAutoScroll(
    contentRevision,
    session.entries.length > 0,
    sessionKey,
  );
  const hasMessages = session.entries.length > 0;
  // The whole transcript is re-uploaded every turn, so it is the browser that
  // knows when the next one will be refused. Warning here, and refusing here,
  // turns a server rejection nobody can act on into a full conversation and a
  // reset button.
  // Two separate ceilings, and hitting either one refuses the next turn. They
  // are told apart only so the message can name the one that was actually hit:
  // "shorten it" is useless advice to someone stopped by the message count.
  const transcriptChars = chatTranscriptChars(session) + (context?.length ?? 0);
  const tooManyChars = transcriptChars >= CHAT_HARD_LIMIT_CHARS;
  const tooManyMessages = chatMessageCount(session) >= CHAT_MAX_MESSAGES;
  const tooLong = tooManyChars || tooManyMessages;
  const nearlyTooLong = !tooLong && transcriptChars >= CHAT_SOFT_LIMIT_CHARS;
  const showsHeading = Boolean(title || onBack);

  useEffect(() => {
    if (active) focusChatInput(inputRef.current);
  }, [active]);

  const emptyScope = disabled || filters.userIds?.length === 0 || filters.instanceIds?.length === 0;
  const send = (text: string, suggestion?: AskSuggestion) => {
    const trimmed = text.trim();
    if (!trimmed || streaming || tooLong || emptyScope) return;
    const messageScope = suggestion?.scope ?? { filters, label: scope.label };
    onSend?.(trimmed, messageScope);
    setDraft("");
    scrollToBottom();
    void sendChatMessage(sessionKey, {
      filters: messageScope.filters,
      scopeLabel: messageScope.label,
      text: trimmed,
      context,
      genericErrorMessage: t("chat_error_generic"),
      formatPublicMessage: message => formatPublicMessage(message, t),
      staticDemoUnavailableMessage: t("chat_static_demo_unavailable"),
    });
  };

  const quoteSelection = (selectedText: string) => {
    setDraft(current => {
      const next = appendQuote(current, selectedText);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(next.length, next.length);
      });
      return next;
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentions.handleKeyDown(event) || event.nativeEvent.isComposing) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send(draft);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {/* The heading row disappears entirely where the surrounding chrome already names the panel;
          pr-9 only matters when a back arrow puts this row beside a collapse button. */}
      {showsHeading && (
        <div className={`flex items-center gap-2 ${onBack ? "pr-9" : ""}`}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("chat_back_to_details")}
              title={t("chat_back_to_details")}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-dim transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {title && <h3 className="truncate text-sm font-semibold text-text">{title}</h3>}
        </div>
      )}

      {/* The top margin only separates this row from a heading. Without one it is the first thing
          in the panel, and the panel's own padding is already the gap. */}
      {!composerScope && (
        <div
          className={`flex items-center gap-1.5 text-xs text-text-dim ${showsHeading ? "mt-2" : ""}`}
        >
          <span>{t("chat_scope_label")}</span>
          <span
            title={scope.label}
            className="min-w-0 break-words rounded-lg border border-outline bg-surface-strong px-2 py-0.5 text-text-mid"
          >
            {scope.label}
          </span>
          <Tooltip.Provider delayDuration={100}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  aria-label={t("chat_ask_tooltip")}
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-dim transition hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="bottom"
                  align="end"
                  sideOffset={6}
                  collisionPadding={12}
                  className="z-[100] max-w-[320px] rounded-md border border-outline bg-surface px-2.5 py-2 text-xs font-normal leading-snug text-text shadow-lg"
                >
                  {t("chat_ask_tooltip")}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
          {/* Sits in this row rather than floating over the (now optional) heading, so it has a
            home whether or not the panel shows one. */}
          {hasMessages && (
            <button
              type="button"
              data-tour="chat-new-conversation"
              onClick={() => {
                resetChat(sessionKey);
                focusChatInput(inputRef.current);
              }}
              aria-label={t("chat_new_conversation")}
              title={t("chat_new_conversation")}
              className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-dim transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      <div className="relative mt-3 min-h-0 flex-1">
        <div
          ref={scrollRef}
          {...scrollProps}
          className="flex h-full flex-col gap-4 overflow-y-auto pr-1"
        >
          {!hasMessages && (suggestions.length > 0 || starterAction) && (
            <div className={`flex flex-col gap-3 py-2 ${welcome ? "" : "mt-auto"}`}>
              {welcome && (
                <div className="mb-3 mt-2">
                  <h3 className="font-display text-xl font-semibold tracking-tight text-text">
                    {welcome.title}
                  </h3>
                </div>
              )}
              {suggestions.map(item => {
                const suggestion = typeof item === "string" ? { label: item } : item;
                const Icon = suggestion.icon ? ASK_ICONS[suggestion.icon] : MessageCircleQuestion;
                const button = (
                  <button
                    type="button"
                    onClick={() => send(suggestion.prompt ?? suggestion.label, suggestion)}
                    disabled={emptyScope || streaming}
                    className="flex w-full items-center gap-3 rounded-xl border border-info/15 bg-info/[0.025] px-4 py-3 text-left text-sm leading-snug text-text-mid transition-colors hover:border-info/35 hover:bg-info/5 hover:text-info focus-visible:outline-info disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-info" aria-hidden="true" />
                    <span className="min-w-0 break-words">
                      {suggestion.label}
                      {suggestion.detail && (
                        <span className="mt-1 block text-xs font-medium leading-relaxed text-text-mid">
                          {suggestion.detail}
                        </span>
                      )}
                    </span>
                  </button>
                );
                return suggestion.detailTitle ? (
                  <Tooltip.Provider key={suggestion.label} delayDuration={200}>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="top"
                          sideOffset={6}
                          className="z-[100] max-w-xs rounded-lg bg-text px-3 py-2 text-xs text-surface shadow-lg"
                        >
                          {suggestion.detailTitle}
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                ) : (
                  <React.Fragment key={suggestion.label}>{button}</React.Fragment>
                );
              })}
              {starterAction && (
                <button
                  type="button"
                  onClick={starterAction.onClick}
                  disabled={emptyScope || streaming}
                  className="mt-2 flex w-full items-center gap-3 rounded-xl border border-info/30 bg-surface px-4 py-3 text-left text-sm font-medium text-info hover:bg-info/5 focus-visible:outline-info disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {starterAction.kind === "kpi" ? (
                    <Lightbulb size={20} aria-hidden="true" />
                  ) : starterAction.kind === "explore" ? (
                    <Route size={20} aria-hidden="true" />
                  ) : (
                    <ArrowLeftRight size={20} aria-hidden="true" />
                  )}
                  <span className="flex-1">{starterAction.label}</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
          {session.entries.map((entry, index) =>
            entry.kind === "scope-note" ? (
              <div key={index} className="text-center text-[11px] text-text-dim">
                {t("chat_scope_changed", { label: entry.label })}
              </div>
            ) : entry.kind === "thinking" ? (
              <div
                key={index}
                className="mr-8 rounded-lg border border-outline bg-surface-strong/60 px-3 py-2 text-xs text-text-dim"
              >
                <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                {t(chatThinkingTranslationKey(entry.thinkingKind))}
              </div>
            ) : entry.role === "user" ? (
              <div
                key={index}
                className="ml-6 rounded-xl border border-info/15 bg-info/10 px-4 py-3 text-sm leading-relaxed text-text"
              >
                <ChatMarkdown content={entry.content} />
              </div>
            ) : (
              <div
                key={index}
                className="rounded-xl border border-outline bg-surface px-4 py-3 text-sm leading-relaxed text-text"
                // The tour waits for a finished reply; a streaming or empty
                // bubble doesn't yet count as "the answer appeared". Only the
                // last entry is ever tagged: an older answer still in the
                // transcript must not satisfy the wait while a new reply is
                // being written under it.
                data-tour={
                  entry.content !== "" && index === session.entries.length - 1 && !streaming
                    ? "chat-answer"
                    : undefined
                }
              >
                {entry.content === "" && streaming && index === session.entries.length - 1 ? (
                  <span className="animate-pulse text-text-dim">…</span>
                ) : (
                  <>
                    <ChatMarkdown content={entry.content} />
                    {!(streaming && index === session.entries.length - 1) &&
                      entry.content !== "" && (
                        <div className="mt-1">
                          <CopyButton content={entry.content} />
                        </div>
                      )}
                  </>
                )}
              </div>
            ),
          )}
        </div>
        {streaming && detached && (
          <DetachedStreamingIndicator onClick={scrollToBottom} label={t("loading_ellipsis")} />
        )}
        <ChatSelectionQuote
          containerRef={scrollRef}
          onQuote={quoteSelection}
          label={t("chat_quote_in_reply")}
        />
      </div>

      {session.status === "error" && session.error && (
        <div className="mt-2 flex items-start justify-between gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger-strong">
          <span className="min-w-0 break-words">{session.error}</span>
          <button
            type="button"
            onClick={() => clearChatError(sessionKey)}
            aria-label={t("chat_dismiss_error")}
            title={t("chat_dismiss_error")}
            className="shrink-0 rounded p-0.5 transition hover:bg-danger/20"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {(nearlyTooLong || tooLong) && (
        <div
          className={`mt-2 flex items-start gap-1.5 rounded-lg border px-3 py-2 text-xs ${
            tooLong
              ? "border-danger/30 bg-danger/10 text-danger-strong"
              : "border-warning/30 bg-warning/10 text-warning-strong"
          }`}
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            {tooManyMessages && !tooManyChars
              ? t("chat_too_many_messages")
              : tooLong
                ? t("chat_too_long")
                : t("chat_getting_long")}
          </span>
        </div>
      )}

      {composerActions}

      {/* One composer box holding both the field and its action, so the pair reads as a single
          control. The focus cue is a border tint rather than a ring: this box is most of what is
          on screen when the panel is empty, and a 2px halo around it dominates the panel. */}
      {/* No left padding: the field owns its own px-3, because QuoteTextarea's quoted-reply
          overlay is positioned against the field and carries that padding as a fixed value. */}
      <div className="relative mt-2 shrink-0 rounded-xl border border-outline bg-surface py-2 transition-colors focus-within:border-info/60">
        {mentions.menu && <MentionMenu {...mentions.menu} placement="above" />}
        {(composerScope || contextItems.length > 0) && (
          <div
            role="group"
            aria-label={t("chat_scope_label")}
            className="mx-3 mb-1 border-b border-outline pb-2 pt-1"
          >
            {composerScope}
            {contextItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" aria-label={t("chat_selected_steps")}>
                {contextItems.map(item => (
                  <span
                    key={item.id}
                    className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border border-primary/25 bg-primary/10 py-0.5 pl-2 pr-1 text-xs text-text-mid"
                  >
                    <span className="truncate" title={item.name}>
                      {truncateChatContextItem(item.name)}
                    </span>
                    {onRemoveContextItem && (
                      <button
                        type="button"
                        onClick={() => onRemoveContextItem(item.id)}
                        aria-label={t("chat_remove_selected_step", {
                          step: item.name,
                        })}
                        title={t("chat_remove_selected_step", {
                          step: item.name,
                        })}
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-dim transition hover:bg-primary/15 hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-1.5 pr-2">
          <QuoteTextarea
            ref={inputRef}
            data-tour="chat-input"
            value={draft}
            onChange={event => {
              setDraft(event.target.value);
              if (mentionOptions) mentions.sync(event.target.value, event.target.selectionStart);
            }}
            onClick={event => {
              if (mentionOptions) mentions.sync(draft, event.currentTarget.selectionStart);
            }}
            onBlur={mentions.close}
            highlightTokens={mentionTokens}
            aria-autocomplete="list"
            aria-controls={mentions.menu?.id}
            aria-activedescendant={
              mentions.menu?.candidates.length
                ? `${mentions.menu.id}-${mentions.menu.activeIndex}`
                : undefined
            }
            onKeyDown={onKeyDown}
            placeholder={t(
              mentionOptions ? "chat_input_mentions_placeholder" : "chat_input_placeholder",
            )}
            rows={2}
            // Cap via the prop the textarea already clamps to, not a max-h-* class — two numbers
            // for one limit drift.
            maxHeight={COMPOSER_MAX_HEIGHT}
            // w-full, not flex-1: QuoteTextarea's wrapper is not a flex container, so a flex child
            // rule does nothing and the textarea falls back to its intrinsic `cols` width. px-3 py-2
            // must match QuoteTextarea's quoted-reply overlay, which hardcodes that padding.
            className="min-h-[2.5rem] w-full resize-none bg-transparent px-3 py-2 text-sm text-text placeholder:text-text-dim focus:outline-none"
          />
          {streaming ? (
            <button
              type="button"
              onClick={() => stopChat(sessionKey)}
              aria-label={t("chat_stop")}
              title={t("chat_stop")}
              className="mb-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-outline text-text-mid transition hover:bg-surface-strong hover:text-text"
            >
              <Square className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              data-tour="chat-send"
              onClick={() => send(draft)}
              disabled={draft.trim() === "" || tooLong || emptyScope}
              aria-label={t("chat_send")}
              title={t("chat_send")}
              className="mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info text-white transition hover:bg-info-strong disabled:cursor-not-allowed disabled:bg-muted-surface disabled:text-text-dim"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <ChatAiDisclaimer />
    </div>
  );
};
